import React, { ReactNode } from 'react';
import { GridChildComponentProps, VariableSizeGrid } from 'react-window';
import { Button, Table, Tooltip } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import AutoSizer from 'react-virtualized-auto-sizer';
import cx from 'classnames';

import { SortButton, SortStatus } from '../sortButton';
import { TableSort } from '../../../utils/dataTable/data';
import { ExtendedColumnProps } from '../../../utils/dataTable/column';
import { IAccountDetails, IUser } from '../../../slices/users/user';
import { UserTypeMap, UserTypeMapForRepDataAccount, UserTypes } from '../domain/userTypes';
import { ScrollPosition } from '../../../slices/utils';
import { UserRoles } from '../domain/userRoles';
import { ACCOUNT_ACCESS } from './interfaces';
import { INVOICE_NUMBER, isInvoice } from '../../../slices/accounts/paymentAndBilling/types';
import { AccessTypes } from '../domain/accountAccessType';
import { Features, isFeatureEnabled } from '../../../featureToggles/features';
import { AutoEllipsisTooltip } from '../autoEllipsisTooltip';
import { isRepDataAccount } from '../../../utils/utils';

import styles from './dataTable.plgexternal.module.scss';

export enum EntityHandlers {
  AccessPermissions = 'EDIT_ACCESS_PERMISSIONS'
}

interface IProps<T extends IDataTableEntity> {
  columns: ExtendedColumnProps<T>[];
  data: T[];
  loading: boolean;
  editingEntity: string;
  withActiveToggle: boolean;
  toggleEditEntityHandler: ((isOpen: boolean, entityToEdit: T, handler: EntityHandlers | null) => void) | null;
  onToggleActiveState?: (entityToEdit: T) => void;
  sortTable: (payload: TableSort) => void;
  sort: TableSort;
  loggedInUser: IUser | null;
  initialScroll?: ScrollPosition;
  noDataMessage?: ReactNode;
  logEvent?: (recordNumber: string) => void;
}

interface IDataTableEntity {
  id: string | number;
  isActive?: boolean;
  status?: string;
  account?: IAccountDetails;
  roles?: Array<string>;
  userType?: UserTypes;
  accountId?: string;
}

interface CustomizeScrollBodyinfo {
  scrollbarSize: number;
  ref: React.Ref<{
    scrollLeft: number;
  }>;
  onScroll: (info: {
    currentTarget?: HTMLElement;
    scrollLeft?: number;
  }) => void;
}

const actionsColumnWidth = 48;
const nameColumnWidth = 184;

export class DataTable<T extends IDataTableEntity> extends React.Component<IProps<T>> {
  constructor(props: IProps<T>) {
    super(props);

    this.renderVirtualList = this.renderVirtualList.bind(this);
    this.handleOptionClick = this.handleOptionClick.bind(this);
  }

  componentDidUpdate(_prevProps: IProps<T>): void {
    this.restoreScrollHeight();
  }

  private restoreScrollHeight(): void {
    const { initialScroll } = this.props;
    const tableContainer = document.getElementsByClassName('ant-table-content');
    if ((!initialScroll?.x && !initialScroll?.y) || !tableContainer.length) {
      return;
    }
    tableContainer[0].scrollTo(initialScroll.x, initialScroll.y);
  }

  private onSort(column: ExtendedColumnProps<T>, sortStatus: SortStatus): void {
    this.props.sortTable({
      key: column.key,
      status: sortStatus
    });
  }

  private renderActionsColumn(value: unknown, record: T): ReactNode {
    return (
      <div className={styles.actionsContainer}>
        {this.renderEditAction(value, record)}
      </div>
    );
  }

  private renderNameColumn(value: unknown, record: T): ReactNode {
    return this.renderEditAction(value, record);
  }

  private renderEditAction(value: unknown, record: T): ReactNode {
    const { loggedInUser } = this.props;
    if (!this.props.toggleEditEntityHandler) {
      return null;
    }
    let message = '';
    let shouldDisable = false;
    if (loggedInUser && loggedInUser.userType === UserTypes.User && record.id !== loggedInUser.id) {
      message = 'You don\'t have the permissions to perform this action';
      shouldDisable = true;
    }

    let iconIU = value;

    if (shouldDisable) {
      iconIU = <Tooltip title={message}>
        {iconIU}
      </Tooltip>;
    }

    return (
      <div className={cx(styles.editIconContainer, styles.nameContainer)}
        onClick={shouldDisable ? undefined : this.props.toggleEditEntityHandler.bind(this, true, record, null)}
      >
        {typeof iconIU === 'string' ? <AutoEllipsisTooltip text={iconIU} tooltipText={iconIU} className={styles.tooltip}/> : iconIU}
      </div>
    );
  }

  private renderDataWithActions(value: unknown, record: T): ReactNode {
    const { loggedInUser, toggleEditEntityHandler } = this.props;
    if (!toggleEditEntityHandler) {
      return null;
    }
    const shouldDisable = loggedInUser && loggedInUser.userType === UserTypes.User && record.id !== loggedInUser.id;
    const message = shouldDisable ? 'You don\'t have the permissions to perform this action' : '';

    return (
      <div className={styles.clickableCell}
        onClick={shouldDisable ? undefined : toggleEditEntityHandler.bind(this, true, record, null)}
      >
        <Tooltip title={message}>
          {value}
        </Tooltip>
      </div>
    );
  }

  private handleOptionClick(recordNumber: string) {
    const { logEvent } = this.props;
    if (logEvent) {
      logEvent(recordNumber);
    }
  }

  private renderInvoiceNumberColumn(_value: unknown, record: T): ReactNode {
    if (!isInvoice(record)) {
      return null;
    }
    const pdfUrl = record.pdfUrl ?? '';
    const invoiceNumber = record.number ?? '';
    return (
      <a
        target='blank'
        href={pdfUrl}
        className={styles.invoiceNumber}
        onClick={() => this.handleOptionClick(record.number ?? '')}
      >
        {invoiceNumber}
      </a>
    );
  }

  private renderHighlightedColumn(value: unknown, record: T): ReactNode {
    const { toggleEditEntityHandler } = this.props;
    return (
      <div className={styles.highlightedColumn} onClick={toggleEditEntityHandler?.bind(this, true, record, null)}>{value}</div>
    );
  }

  private renderAccountAccessColumn(_value: unknown, record: T): ReactNode {
    const userRoles = record.roles ?? [];
    const accountId = record.accountId ?? '';
    const isOwner = record.account?.accessType !== AccessTypes.Enterprise && !!userRoles.find((role) => role === UserRoles.CompanyOwner);
    const userType = isRepDataAccount(accountId) ? UserTypeMapForRepDataAccount.get(record.userType || UserTypes.User) : UserTypeMap.get(record.userType || UserTypes.User);
    return <div className={cx(styles.accessBubble, {
      [styles.isOwner]: isOwner
    })}>{isOwner ? 'Owner' : userType}</div>;
  }

  private renderAccessColumn(_value: unknown, record: T): ReactNode {
    const { toggleEditEntityHandler } = this.props;
    return <div className={styles.buttonContainer}>
      <Button
        className={styles.button}
        type='link'
        onClick={() => toggleEditEntityHandler?.(true, record, EntityHandlers.AccessPermissions)}
      >
        Edit access
      </Button>
    </div >;
  }

  private renderIsActiveToggle(_value: unknown, record: T): ReactNode {
    const { editingEntity, withActiveToggle, loggedInUser } = this.props;

    if (
      !withActiveToggle
      || (loggedInUser && loggedInUser.userType === UserTypes.User)
    ) {
      return null;
    }

    if (editingEntity && editingEntity === record.id) {
      return <LoadingOutlined />;
    }
    const isActive = record.status === 'active';
    const switchUI = <div
      className={cx(styles.activeToggle, {
        [styles.inactive]: !isActive
      })}
    >
      <span className={styles.circle} />{isActive ? 'Active' : 'Lapsed'}
    </div>;

    return <div>{switchUI}</div>;
  }

  private renderColumnSorter(colDefinition: ExtendedColumnProps<T>): ReactNode {
    if (!colDefinition.sorter) {
      return null;
    }
    const { sort } = this.props;
    const sorterDefinition = {
      ...colDefinition,
      key: colDefinition.sorterKey || colDefinition.key
    };
    const status = sort.key === sorterDefinition.key ? sort.status : SortStatus.None;
    return <SortButton
      status={status}
      onSort={this.onSort.bind(this, sorterDefinition)}
      noSortClassName={cx(styles.sortIcon, styles.disabled)}
      sortDownClassName={styles.sortIcon}
      sortUpClassName={styles.sortIcon}
    />;
  }

  private renderColumnTitle(colDefinition: ExtendedColumnProps<T>): ReactNode {
    return <div className={cx(styles.titleContainer, {
      [styles.actionsColumnTitle]: colDefinition.isActionsColumn
    })}>
      <div className={cx(styles.columnTitle)}>{colDefinition.title}</div>
      {this.renderColumnSorter(colDefinition)}
    </div>;
  }

  private renderColumnData(rawData: T[], { columnIndex, rowIndex, style }: GridChildComponentProps) {
    const { columns = [] } = this.props;
    const record: T = rawData[rowIndex];
    const columnDataIndex = columns[columnIndex].dataIndex as keyof T;

    if (!(columnDataIndex in record)) {
      return null;
    }

    return <div style={style} key={`r_${rowIndex}_c_${columnIndex}`}>
      {record[columnDataIndex]}
    </div>;
  }

  private getColumnWidth(columnIndex: number, totalWidth: number, columnsCount: number): number {
    const lastColumnIndex = columnsCount - 1;
    return columnIndex === lastColumnIndex ? actionsColumnWidth : (totalWidth - actionsColumnWidth) / (lastColumnIndex);
  }

  private renderVirtualList(rawData: T[], { onScroll }: CustomizeScrollBodyinfo): ReactNode {
    const { columns = [] } = this.props;
    return <AutoSizer>
      {({ height, width }) => (
        <VariableSizeGrid
          columnCount={columns.length}
          columnWidth={(index) => this.getColumnWidth(index, width, columns.length)}
          rowCount={rawData.length}
          rowHeight={() => 54}
          height={height}
          width={width}
          onScroll={({ scrollLeft }) => {
            onScroll({ scrollLeft });
          }}
        >
          {this.renderColumnData.bind(this, rawData)}
        </VariableSizeGrid>
      )}
    </AutoSizer>;
  }

  private getLocaleConfiguration() {
    const { noDataMessage } = this.props;
    return noDataMessage ? { emptyText: noDataMessage } : undefined;
  }

  render(): ReactNode {
    const { columns = [], data = [], loading, editingEntity } = this.props;
    const columnsWithHandlers = columns.map(col => {
      let renderMethod = col.render;

      if (col.mapper) {
        renderMethod = col.mapper;
        if (col.render) {
          renderMethod = (value, ...args) => col.render?.(col.mapper?.(value), ...args);
        }
      }

      if (col.isHighLightedColumnClickable) {
        renderMethod = this.renderHighlightedColumn.bind(this);
      }

      if (col.key === INVOICE_NUMBER) {
        renderMethod = this.renderInvoiceNumberColumn.bind(this);
      }

      if (col.key === ACCOUNT_ACCESS) {
        renderMethod = this.renderAccountAccessColumn.bind(this);
      }

      if (col.key === 'access') {
        renderMethod = this.renderAccessColumn.bind(this);
      }

      if (col.key === 'accountStatus') {
        renderMethod = this.renderIsActiveToggle.bind(this);
      }

      if (col.isActionsColumn) {
        renderMethod = this.renderActionsColumn.bind(this);
      }

      if (col.showDataWithAction) {
        renderMethod = this.renderDataWithActions.bind(this);
      }

      let width: string | number | undefined = col.isActionsColumn ? `${actionsColumnWidth}px` : undefined;

      if (isFeatureEnabled(Features.VideoTracking)) {
        width = col.isActionsColumn ? `${actionsColumnWidth}px` : (col.width ?? undefined);
      }

      if (col.autoWidth) {
        width = undefined;
      }

      if (col.isClickableColumn) {
        renderMethod = this.renderNameColumn.bind(this);
        width = `${nameColumnWidth}px`;
      }

      return {
        ...col,
        title: this.renderColumnTitle.bind(this, col),
        render: renderMethod,
        sorter: undefined,
        width: width,
        className: cx(styles.dataTableCell, {
          [styles.rightActionCell]: col.isActionsColumn,
          [styles.autoWidth]: col.autoWidth
        })
      };
    });

    if (loading && !editingEntity) {
      return <div className={styles.loadingContainer}>
        <LoadingOutlined className={styles.loadingSpin} />
      </div>;
    }

    return <Table<T>
      columns={columnsWithHandlers}
      rowKey="id"
      dataSource={data}
      pagination={false}
      scroll={{ x: 'max-content' }}
      components={{ body: this.renderVirtualList }}
      className={styles.dataTable}
      locale={this.getLocaleConfiguration()}
    />;
  }
}
